import React, {Component, useEffect, useMemo, useState} from "react";
import {AgGridReact} from "ag-grid-react";
import {
    AllCommunityModule,
    ColumnMenuModule,
    ColumnsToolPanelModule,
    ContextMenuModule,
    ServerSideRowModelModule,
    SetFilterModule,
    MultiFilterModule,
} from "ag-grid-enterprise";
import {Helmet} from "react-helmet";
import {Link, useOutletContext} from "react-router-dom";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {defaultZenGroupColumnInitWithOptionsWithValueGetter} from "../../utils/zenGroupDisplayNameGridHelper";
import {defaultAgentNameColumnInitWithOptions} from "../../utils/agentNameGridHelper";
import {dateValueFormatter} from "../../utils/gridDateFormatter";
import {agentVersionFormatter} from "../../utils/agentVersionFormatter";
import {getDateStringForAgGridFilter} from "./incidents";
import {
    fortressAiReportListReactive,
    findByFortressAiReportIdListReactive,
    updateFortressAiReportGridUseFilterStateReactive,
    updateFortressAiReportGridColumnStateReactive,
    updateFortressAiReportGridColumnModeReactive,
    updateFortressAiReportGridFilterModelReactive,
    updateFortressAiReportGridUseColumnStateReactive,
    changeFortressAiReportNameReactive
} from "../api/fortressAiReportApi"
import {refreshGridZenGroupAndAgentInformation} from "../../utils/refreshGridHelper";
import {masterFilterHelper, serverSideFilterHelperHandleMultiConditionChecks} from "../../utils/filterHelper";
import {
    getColumnModeInSession,
    getDefaultAgGridSidebarProps,
    getUseColumnStateInSession,
    getUseFilterStateInSession,
    onColumnStateChangedHelper,
    onFilterChangedHelper,
    onGridReadyHelper,
    onGridReadyHelperForColumnState,
    updateColumnModeInSessionHelper,
    updateUseColumnStateHelper,
    updateUseFilterStateHelper
} from "../../utils/gridFilterStateAndColumnStateHelper";
import {ClearRefresh} from "../../components/clearRefreshButtons";
import CustomNameCellEditor, {getEditIconComponent} from "../../utils/customCellEditor";
import DTPicker, {dateFilterParametersInHeaderSuppressSorting, dateFilterParametersInHeaderClientSideGrid} from "../../utils/DTPicker";
import {GridColumnFilterStateSaving} from "../../components/columnfilterComponent";
import privatePageHeaderHelper from "../../utils/privatePageHeaderHelper";
import {useLocation} from "react-router-dom";
import {findZenGroupById} from "../../utils/zenGroupSessionStorageManager";
import {
    ClickToShowColumnOptionsWithToggleButtonGroup,
    customColumnModeText,
    mediumColumnModeText,
    minColumnModeText,
    standardApplyMinimumOrMediumColumnMode
} from "../../components/clickToShowButtons";
import {getItemFromStorageWithoutDecrypting, decryptAndGetSessionVariable} from "../../utils/storageHelper";
import {untrustedModuleLoadCellRenderer, untrustedModuleLoadValueFormatter} from "../../utils/moduleLoadFormatter";
import {DisplayOptions} from "../../components/displayOptionsComponent";
import {MuiIconButtonWithTooltip, MuiCloseIconButton} from "../../components/muiComponents";
import Modal from "react-modal";
import NotificationManager from "react-notifications/lib/NotificationManager";
import {ClientSideRowModelModule} from "ag-grid-community";
import {handleGroupColumnChangeNameOnly} from "../../utils/gridCellEditing";
import {changeAgentNameReactive} from "../api/agentsApi";

let gridColumnStateSessionVariableName = "fortressAiReportGridColumnState"
let gridFilterStateSessionVariableName = "fortressAiReportGridFilterState"
let minColumnIds = ["zenGroupDisplayName", "machineName", "aiTool", "path", "dateCreated"]
let medColumnIds = ["zenGroupDisplayName", "machineName", "incidentDisplayName", "accessedFilePath", "path", "programArguments", "parentPath", "parentProgramArguments", "dateCreated"]

const processData = (data) => {
    let avgMemString = ""
    const avgMemVal = data.avgMemory
    avgMemString = avgMemVal;
    if (avgMemVal > 1000) {
        avgMemString = (avgMemVal / 1000).toFixed(2) + " KB"
    }
    if (avgMemVal > 1000000) {
        avgMemString = (avgMemVal / 1000000).toFixed(2) + " MB"
    }
    if (avgMemVal > 1000000000) {
        avgMemString = (avgMemVal / 1000000000).toFixed(2) + " GB"
    }

    let pidReuse = ""
    let bootGuid = ""
    if(data.uniqueProcessIdentifier !== null && data.uniqueProcessIdentifier !== undefined){
        pidReuse = data.uniqueProcessIdentifier.reuseCount
        bootGuid = data.uniqueProcessIdentifier.bootGuid
    }

    const aiToolMapping = {
        'CHAT_GPT': 'ChatGPT',
        'CLAUDE': 'Claude',
        'COPILOT': 'Copilot',
        'GENSPARK': 'Genspark',
        'COMET': 'Perplexity',
        'MAXATHON': 'Maxathon',
        'OTHER': 'Other'
    };
    const formattedAiTool = aiToolMapping[data.aiTool] || 'Other';

    let gridData = {
        "Report Name": data.incidentDisplayName,
        "Machine Name": data.machineName,
        "AI Tool": formattedAiTool,
        "Accessed File Path": data.accessedFilePath,
        "Path": data.path,
        "Program Arguments": data.programArguments,
        "Parent Path": data.parentPath,
        "Parent Program Arguments": data.parentProgramArguments,
        "Ram Usage": avgMemString,
        "Pid Reuse Number": pidReuse,
        "Process Execution GUID": bootGuid,
        "Username": data.username,
        "User SID": data.userSid,
    };
    return Object.keys(gridData).map((key) => ({
        description: key,
        value: gridData[key],
    }))
};

export default function FortressAiReport() {
    const { isLoading, setIsLoading } = useOutletContext();
    const [gridApi, setGridApi] = useState();
    const fortressAiReportLocation = useLocation();
    const [isCyberCrucibleUser, setIsCyberCrucibleUser] = useState(getItemFromStorageWithoutDecrypting("username")?.endsWith("@cybercrucible.com"));
    const [useFilterStateSettingToggled, setUseFilterStateSettingToggled] = useState(getUseFilterStateInSession(gridFilterStateSessionVariableName));
    const [useColumnStateSettingToggled, setUseColumnStateSettingToggled] = useState(getUseColumnStateInSession(gridColumnStateSessionVariableName));
    const [columnMode, setColumnMode] = useState(getColumnModeInSession(gridColumnStateSessionVariableName));
    const [clickedIconIncidentData, setClickedIconIncidentData] = useState();
    const [openDetailsModal, setOpenDetailsModal] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [columnDefs, setColumnDefs] = useState([
        defaultZenGroupColumnInitWithOptionsWithValueGetter(false, true, true),
        {
            field: "incidentDisplayName",
            headerName: "Name",
            initialWidth: 350,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                maxNumConditions: 2,
            },
            sortable: true,
            editable: true,
            cellEditor: 'customNameCellEditor',
            cellRenderer: function(params){
                return(
                    <div className={"flex flex-nowrap items-center justify-start gap-x-1"}>
                        <MuiIconButtonWithTooltip
                            icon={
                                <FontAwesomeIcon
                                    className={"self-center object-contain"}
                                    icon="fa-duotone fa-binoculars"
                                    size="xs"
                                />
                            }
                            tooltipTitle={"Click to view details"}
                            tooltipPlacement={"bottom-start"}
                            onClick={() => {
                                initializeDetailsModalForIconClick(params.node.data)
                            }}
                        />
                        {getEditIconComponent(params, "Click to Edit this Report's Name", "incidentDisplayName")}
                        {params.value}
                    </div>
                )
            },
        },
        defaultAgentNameColumnInitWithOptions(false, 5),
        {
            field: "zenGroupId",
            hide: true,
            suppressColumnsToolPanel: true,
            lockVisible: true
        },
        {
            field: "machineName",
            headerName: "Machine Name",
            initialWidth: 225,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                maxNumConditions: 5,
            },
            cellRenderer: function(params){
                const machineNameOnRender = params.node.data.machineName
                let agentLinkDiv = ""
                if(params.data.agentId){
                    agentLinkDiv =
                        <Link to={{pathname:"/private/agents"}} state={{
                            agentDisplayNameClicked: params.node.data.agentDisplayName,
                            zenGroupIdClicked: params.node.data.zenGroupId,
                            agentIdClicked: params.node.data.agentId,
                            machineNameClicked: params.node.data.machineNameForLocationLink
                        }} className="">
                            <MuiIconButtonWithTooltip
                                icon={
                                    <FontAwesomeIcon
                                        className="object-contain"
                                        icon="fa-duotone fa-user-gear"
                                        size="xs"
                                    />
                                }
                                tooltipTitle={"Click to Manage This Agent"}
                                tooltipPlacement={"bottom-start"}
                            />
                        </Link>
                }
                return(
                    <div className={"flex flex-nowrap items-center gap-x-2"}>
                        {agentLinkDiv}
                        {machineNameOnRender}
                    </div>
                )
            },
            sortable: true,
            tooltipField: "machineName",
        },
        {
            headerName: "AI Tool",
            field: "aiTool",
            width: 200,
            sortable: true,
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
                values: ['CHAT_GPT', 'CLAUDE', 'COPILOT', 'GENSPARK', 'COMET', 'MAXATHON', 'OTHER'],
                valueFormatter: (params) => {
                    const mapping = {
                        'CHAT_GPT': 'ChatGPT',
                        'CLAUDE': 'Claude',
                        'COPILOT': 'Copilot',
                        'GENSPARK': 'Genspark',
                        'COMET': 'Perplexity',
                        'MAXATHON': 'Maxathon',
                        'OTHER': 'Other'
                    };
                    return mapping[params.value] || 'Other';
                }
            },
            valueFormatter: (params) => {
                const mapping = {
                    'CHAT_GPT': 'ChatGPT',
                    'CLAUDE': 'Claude',
                    'COPILOT': 'Copilot',
                    'GENSPARK': 'Genspark',
                    'COMET': 'Perplexity',
                    'MAXATHON': 'Maxathon',
                    'OTHER': 'Other'
                };
                return mapping[params.value] || 'Other';
            }
        },
        {
            field: "reportMode",
            headerName: "Report Mode",
            initialWidth: 230,
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
                values: ["ON", "SIMULATE"],
                suppressSorting: true,
                convertValuesToStrings: true,
                valueFormatter: (params) => {
                    if (params.value === "SIMULATE") return "Simulate"
                    return "Block"
                },
            },
            sortable: true,
            valueFormatter: (params) => {
                if (params.value === "SIMULATE") return "Simulate"
                return "Block"
            },
            cellRenderer: function (params) {
                return (
                    <div className={`flex flex-row items-center gap-x-1`}>
                        <MuiIconButtonWithTooltip
                            icon={
                                <FontAwesomeIcon
                                    className="object-contain"
                                    icon="fa-light fa-circle-info"
                                    size="xs"
                                />
                            }
                            tooltipTitle={params.value === "SIMULATE"
                                ? "Report Mode reflects the Fortress AI Report Mode of the group when this report was created. Simulate means no block action was taken — this report is showing you what would have been blocked had Report Mode been set to Block."
                                : "Report Mode reflects the Fortress AI Report Mode of the group when this report was created. Block means the agent actively enforced access policies for this report."
                            }
                            tooltipPlacement={"bottom-start"}
                        />
                        {params.valueFormatted}
                    </div>
                )
            }
        },
        {
            field: "accessedFilePath",
            headerName: "Accessed File Path",
            initialWidth: 500,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                maxNumConditions: 2,
            },
            sortable: true
        },
        {
            field: "path",
            headerName: "Path",
            initialWidth: 600,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                maxNumConditions: 2,
            },
            sortable: true
        },
        {
            field: "programArguments",
            headerName: "Program Arguments",
            initialWidth: 700,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'startsWith'],
                maxNumConditions: 2,
            },
            sortable: true
        },
        {
            field: "parentPath",
            headerName: "Parent Path",
            initialWidth: 600,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                maxNumConditions: 2,
            },
            sortable: true
        },
        {
            field: "parentProgramArguments",
            headerName: "Parent Arguments",
            initialWidth: 700,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'startsWith'],
                maxNumConditions: 2,
            },
            sortable: true
        },
        {
            field: "dateCreated",
            headerName: "Date Created",
            initialWidth: 280,
            filter: 'agDateColumnFilter',
            filterParams: dateFilterParametersInHeaderSuppressSorting(2),
            sortable: true,
            valueFormatter: dateValueFormatter
        },
        {
            field: "dateCollected",
            headerName: "Date Collected",
            initialWidth: 280,
            filter: 'agDateColumnFilter',
            filterParams: dateFilterParametersInHeaderSuppressSorting(2),
            sortable: true,
            valueFormatter: dateValueFormatter
        },
        {
            field: "untrustedModuleLoad",
            headerName: "Untrusted Module Load",
            width: 300,
            sortable: false,
            valueFormatter: function (params) {
                return untrustedModuleLoadValueFormatter(params, params.node.data.uniqueProcessIdentifier)
            },
            cellRenderer: function(params){
                return untrustedModuleLoadCellRenderer(params, params.node.data.uniqueProcessIdentifier)
            },
        },
        { field: "isScreenshotAccess", headerName: "Is Screenshot Access", initialWidth: 230,
            filter: 'agSetColumnFilter',
            filterParams: {
                buttons: ["reset", "apply", "cancel"],
                valueFormatter: function (params) {
                    if (params && params.value) {
                        if(params.value === "true"){
                            return "True"
                        }
                        else{
                            return "False"
                        }
                    }
                    return "False"
                },
                values: ['true', 'false'],
                suppressSorting: false,
            },
            valueFormatter: function (params) {
                if (params && params.value) {
                    if(params.value === true){
                        return "True"
                    }
                    else{
                        return "False"
                    }
                }
                return "False"
            },
            sortable: true
        },
        {
            field: "uniqueProcessIdentifier.bootGuid",
            headerName: "Process Execution GUID",
            initialWidth: 375,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                maxNumConditions: 2,
            },
            sortable: true,
            cellRenderer: function (params) {
                return (
                    <div className="flex flex-row items-center gap-x-1">
                        <div className={""}>
                            <MuiIconButtonWithTooltip
                                icon={
                                    <FontAwesomeIcon
                                        className="object-contain"
                                        icon="fa-light fa-circle-info"
                                        size="xs"
                                    />
                                }
                                tooltipTitle={"The process execution guid is a unique identifier for the time period between boot and shutdown/restart. Pid reuse numbers are unique per boot guid."}
                                tooltipPlacement={"bottom-start"}
                            />
                        </div>
                        {params.value}
                    </div>
                )
            }
        },
        {
            field: "uniqueProcessIdentifier.reuseCount",
            headerName: "Pid Reuse Number",
            initialWidth: 250,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['equals'],
                maxNumConditions: 2,
            },
            sortable: false
        },
        {
            field: "userSid",
            headerName: "User Sid",
            hide: true,
            sortable: true,
            initialWidth: 500,
            suppressColumnsToolPanel: true,
            lockVisible: true,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains'],
                maxNumConditions: 2,
            }
        },
        {
            field: "username",
            headerName: "Username",
            initialWidth: 320,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                maxNumConditions: 2,
            },
            sortable: true
        },
        {
            field: "agentVersionFormatted",
            headerName: "Agent Version",
            initialWidth: 280,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                maxNumConditions: 2,
            },
            sortable: true,
            valueFormatter: agentVersionFormatter,
            cellRenderer: function(params){
                let spinnerDiv = ""
                if(params.node.data.agentId === params.valueFormatted ){
                    spinnerDiv = <FontAwesomeIcon
                        className="contain fa-pulse"
                        icon="fa-light fa-spinner"
                        size="lg"
                        name="agentVersionLoading"
                    />
                }else{
                    spinnerDiv = ""
                }
                return(
                    <div id ="fortooltip" className={"flex flex-nowrap items-center justify-start gap-x-2"}>
                        {spinnerDiv}
                        {params.valueFormatted}
                    </div>
                )
            },
        },
        {
            field: "currentLatestAgentVersion",
            headerName: "Latest Agent Version",
            initialWidth: 275,
            filter: 'agTextColumnFilter',
            filterParams: {
                suppressSorting: true,
                buttons: ["reset", "apply"],
                filterOptions: ['contains', 'notContains', 'equals', 'startsWith', 'endsWith'],
                maxNumConditions: 2,
            },
            sortable: true,
        },
        // Hidden fields for filtering
        {
            field: "uniqueProcessIdentifier",
            headerName: "Unique Process Identifier",
            initialWidth: 280,
            filter: 'agTextColumnFilter',
            hide: true,
            suppressColumnsToolPanel: true,
            lockVisible: true
        },
    ])

    const [defaultColDef, setDefaultColDef] = useState(
        {
            resizable: true,
            filterParams: null,
            floatingFilter: true,
            headerClass: "border-0 border-b-0",
            cellClass: "outline:none",
            enableCellChangeFlash: true,
            autoHeight: false,
            cellDataType: false
        }
    )
    const sideBar = useMemo(() => {
        return getDefaultAgGridSidebarProps(325)
    }, []);
    const rowSelection = useMemo(() => {
        return {
            mode: 'singleRow',
            enableClickSelection: true,
            checkboxes: false,
            headerCheckbox: false
        };
    }, []);

    function initializeDetailsModalForIconClick(incidentData) {
        setClickedIconIncidentData(incidentData)
        setOpenDetailsModal(true)
    }

    return (
        <div className="flex flex-col h-full">
            <Helmet>
                <meta charSet="utf-8" />
                <meta name="viewport" content="width=device-width, initial-scale=1" />
                <title>Fortress AI Report</title>
                <script src="https://js.stripe.com/v3/"/>
                <link href="https://fonts.googleapis.com/css2?family=Open+Sans:ital,wght@0,300;0,400;0,600;0,700;0,800;1,300;1,400;1,600;1,700;1,800&display=swap" rel="stylesheet"/>
            </Helmet>
            <div className="flex flex-1 flex-col flex-nowrap gap-y-3 h-full">
                {privatePageHeaderHelper("Fortress AI Report")}
                <hr className="h-0.5" />
                <DisplayOptions useFilterStateSettingToggled={useFilterStateSettingToggled}
                                setUseFilterStateSettingToggled={setUseFilterStateSettingToggled}
                                toggleUpdateUseFilterState={toggleUpdateUseFilterState}
                                useColumnStateSettingToggled={useColumnStateSettingToggled}
                                setUseColumnStateSettingToggled={setUseColumnStateSettingToggled}
                                toggleUpdateUseColumnState={toggleUpdateUseColumnState}
                />
                <div className={"flex flex-row flex-wrap gap-y-3 gap-x-8 self-end justify-end"}>
                    <ClickToShowColumnOptionsWithToggleButtonGroup
                        columnMode={columnMode} setColumnMode={setColumnMode} gridColumnStateSessionVariableName={gridColumnStateSessionVariableName} gridApi={gridApi}
                        minColumnIds={minColumnIds} medColumnIds={medColumnIds} updateGridColumnModeFunction={updateFortressAiReportGridColumnModeReactive} />
                    <ClearRefresh gridApi = {gridApi} refreshGridFunction = {refreshGrid}/>
                </div>
                <div className="h-full flex flex-col gap-y-5" id="gridRoot">
                    {getGrid()}
                </div>
            </div>
            <Modal contentLabel="Report Details" isOpen={openDetailsModal}
                   onRequestClose={() => {
                       setClickedIconIncidentData(null);
                       setOpenDetailsModal(false);
                   }} shouldCloseOnOverlayClick={true}
                   className={`focus:outline-none focus:shadow-sm border-2 flex relative z-50 bg-white w-6xl max-w-6xl inset-y-10 mx-auto rounded-2xl overflow-auto`}
                   overlayClassName="z-50 bg-black bg-opacity-5 fixed inset-0 overflow-scroll"
            >
                <div className="flex flex-col w-full">
                    <div className="flex flex-row justify-between p-8">
                        <h1 className="font-bold text-3xl">Report Details</h1>
                        <MuiCloseIconButton
                            onClick={() => {
                                setClickedIconIncidentData(null);
                                setOpenDetailsModal(false);
                            }}
                        />
                    </div>
                    <hr className="h-0.5 ml-8 mr-8"/>
                    <div className="flex flex-1 flex-col p-8 w-full">
                        <DetailsGrid
                            headers={[
                                {field: "description", headerName: "Description", initialWidth: 65},
                                {field: "value", headerName: "Value"}
                            ]}
                            data={
                                clickedIconIncidentData
                                    ? processData(clickedIconIncidentData)
                                    : []
                            }
                            rowSelection={rowSelection}
                        />
                    </div>
                </div>
            </Modal>
        </div>
    );

    function toggleUpdateUseFilterState(toggleSetting){
        updateUseFilterStateHelper(toggleSetting, gridFilterStateSessionVariableName, updateFortressAiReportGridUseFilterStateReactive);
    }
    function toggleUpdateUseColumnState(toggleSetting){
        updateUseColumnStateHelper(toggleSetting, gridColumnStateSessionVariableName, updateFortressAiReportGridUseColumnStateReactive);
    }

    function getGrid(){
        return (
            <Grid
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                sideBar={sideBar}
                rowSelection={rowSelection}
                setGridApi={setGridApi}
                fortressAiReportLocation={fortressAiReportLocation}
                columnMode={columnMode}
                setColumnMode={setColumnMode}
            />
        );
    }

    async function refreshGrid(){
        await refreshGridZenGroupAndAgentInformation(gridApi, "fortressAiReportId", "fortressAiReportId", findByFortressAiReportIdListReactive, ["currentLatestAgentVersion", "incidentDisplayName"], "agentId", null)
    }
}

function getDatasourceForGrid(localShowAllTelemetryToggled){
    return {
        getRows: async function (params) {
            try{
                let start = params.request.startRow;
                let end = params.request.endRow;
                let singleConditionFilters = masterFilterHelper(params.request.filterModel, true)
                let multiConditionFilters = serverSideFilterHelperHandleMultiConditionChecks(params.request.filterModel)
                let sortModel = params.request.sortModel;
                let sortModelForRequest = null;
                if(sortModel && sortModel.length > 0){
                    try{
                        if(sortModel[0].colId && sortModel[0].sort){
                            sortModelForRequest = sortModel[0]
                        }
                    }
                    catch(error){
                        console.log("Error generating sort request")
                    }
                }
                let count = end - start;
                let page = Math.floor(start / count)
                let objects = await fortressAiReportListReactive(count, page, singleConditionFilters, multiConditionFilters, sortModelForRequest, localShowAllTelemetryToggled);
                let finalPage = true
                if(objects && objects.length === count){
                    finalPage = false
                }
                let lastIdx = undefined
                if (finalPage) {
                    lastIdx = (count * page) + objects.length
                }
                params.api.deselectAll();
                const success = (objects.length !== 0);
                if (success) {
                    params.api.hideOverlay()
                    params.api.setGridOption("loading", false)
                    params.success({
                        rowData: objects, rowCount: lastIdx
                    })
                } else {
                    params.success({
                        rowData: objects, rowCount: lastIdx
                    })
                    params.fail();
                }
            }
            catch(error){
                params.api.showNoRowsOverlay();
                params.fail();
            }
        }
    }
}

let saveFilterChanges = true
class Grid extends Component {
    constructor(props, onClickRow, filterVals, setGridApi) {
        super(props);
    }
    onFirstDataRendered = (params) => {

    };
    onColumnStateChanged = (params) => {
        if(params.source !== "api" && params.source !== "gridOptionsChanged"){
            this.props.setColumnMode && this.props.setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, updateFortressAiReportGridColumnStateReactive)
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, updateFortressAiReportGridColumnModeReactive, customColumnModeText)
        }
        else if(params.source === "api" && params.type === "sortChanged"){
            this.props.setColumnMode && this.props.setColumnMode(customColumnModeText)
            onColumnStateChangedHelper(params, gridColumnStateSessionVariableName, updateFortressAiReportGridColumnStateReactive)
            updateColumnModeInSessionHelper(gridColumnStateSessionVariableName, updateFortressAiReportGridColumnModeReactive, customColumnModeText)
        }
    }
    getContextMenuItems = (params) => {
        return [
            "resetColumns",
            "autoSizeAll"
        ];
    };

    onGridReady = async (params) => {
        ["keyup","keydown"].forEach((event) => {
            window.addEventListener(event, (e) => {
                document.onselectstart = function() {
                    return !(e.shiftKey || e.ctrlKey);
                }
            });
        });

        this.gridApi = params.api;
        this.props.setGridApi(params.api);

        let location = this.props.fortressAiReportLocation

        if (location && location.state && location.state.zenGroupIdClicked
            && location.state.uniqueProcessIdentifier && location.state.path) {
            let zenGroupId = location.state.zenGroupIdClicked
            let uniqueProcessIdentifier = location.state.uniqueProcessIdentifier
            let locationFilterModel = {
                "uniqueProcessIdentifier": {
                    filterType: "text",
                    type: "equals",
                    filter: JSON.stringify(uniqueProcessIdentifier)
                }
            }
            let zenGroup = findZenGroupById(zenGroupId)
            if (zenGroup && zenGroup.friendlyName) {
                locationFilterModel["zenGroupDisplayName"] = {filterType: "set", values: [zenGroup.friendlyName]}
            }
            let path = location.state.path
            if (path) {
                locationFilterModel["path"] = {
                    filterType: "text",
                    type: "equals",
                    filter: path.toLowerCase()
                }
            }
            saveFilterChanges = false
            params.api.setFilterModel(locationFilterModel)
            window.scroll({behavior: "smooth", top: 0, left: 0})
            window.history.replaceState(location.state, '')
        } else {
            const locState            = this.props.fortressAiReportLocation?.state
            const drilldownAiTool     = locState?.drilldownAiTool
            const drilldownUsername   = locState?.drilldownUsername
            const drilldownEpochMs    = locState?.drilldownEpochMs
            const drilldownIsHourly   = locState?.drilldownIsHourly
            const drilldownZenGroupId = locState?.drilldownZenGroupId

            if (drilldownAiTool || drilldownUsername || drilldownEpochMs) {

                const drilldownFilterModel = {}

                if (drilldownAiTool) {
                    drilldownFilterModel["aiTool"] = {
                        filterType: "set",
                        values: [drilldownAiTool]
                    }
                }

                if (drilldownUsername) {
                    drilldownFilterModel["username"] = {
                        filterType: "text",
                        type: "equals",
                        filter: drilldownUsername
                    }
                }

                if (drilldownEpochMs) {
                    const ms = drilldownEpochMs

                    // epochMs is already UTC-rounded to the hour/day boundary.
                    // Build the end of the window in UTC, then convert each boundary
                    // independently through getDateStringForAgGridFilter.
                    const windowEndMs = drilldownIsHourly
                        ? ms + (60 * 60 * 1000) - 1
                        : ms + (24 * 60 * 60 * 1000) - 1

                    const dateFrom = getDateStringForAgGridFilter(ms, 0)
                    const dateTo   = getDateStringForAgGridFilter(windowEndMs, 59)

                    if (dateFrom && dateTo) {
                        drilldownFilterModel["dateCollected"] = {
                            filterType: "date",
                            type: "inRange",
                            dateFrom,
                            dateTo,
                        }
                    } else {
                        console.warn("dateFrom or dateTo is null — dateCollected filter NOT applied")
                    }
                }

                if (drilldownZenGroupId) {
                    const zenGroup = findZenGroupById(drilldownZenGroupId)
                    if (zenGroup && zenGroup.friendlyName) {
                        drilldownFilterModel["zenGroupDisplayName"] = {
                            filterType: "set",
                            values: [zenGroup.friendlyName]
                        }
                    }
                }

                saveFilterChanges = false
                params.api.setFilterModel(drilldownFilterModel)
                window.history.replaceState({}, '', window.location.pathname)
                window.scroll({behavior: "smooth", top: 0, left: 0})
            } else {
                saveFilterChanges = true
                onGridReadyHelper(params, gridFilterStateSessionVariableName)
            }
        }

        let columnMode = this.props.columnMode
        if (columnMode === customColumnModeText) {
            onGridReadyHelperForColumnState(params, gridColumnStateSessionVariableName)
        } else if (columnMode === minColumnModeText) {
            standardApplyMinimumOrMediumColumnMode(gridColumnStateSessionVariableName, params.api, this.props.setColumnMode, minColumnModeText, minColumnIds, updateFortressAiReportGridColumnModeReactive)
        } else if (columnMode === mediumColumnModeText) {
            standardApplyMinimumOrMediumColumnMode(gridColumnStateSessionVariableName, params.api, this.props.setColumnMode, mediumColumnModeText, medColumnIds, updateFortressAiReportGridColumnModeReactive)
        }

        let dataSource = getDatasourceForGrid(true)
        params.api.setGridOption("serverSideDatasource", dataSource);
    };

    render() {
        return (
            <div className={"w-full h-full"} style={{minHeight: "400px"}}>
                <div id="myGrid" className="ag-theme-alpine rounded-md shadow h-full w-full">
                    <AgGridReact
                        columnMenu={"legacy"}
                        modules={[
                            ServerSideRowModelModule,
                            AllCommunityModule,
                            ColumnMenuModule,
                            ContextMenuModule,
                            ColumnsToolPanelModule,
                            SetFilterModule,
                            MultiFilterModule
                        ]}
                        defaultColDef={this.props.defaultColDef}
                        columnDefs={this.props.columnDefs}
                        getRowId={(params) => {
                            return params.data.fortressAiReportId;
                        }}
                        suppressOverlays={["noRows"]}
                        suppressExcelExport={true}
                        suppressCsvExport={true}
                        suppressMultiSort={true}
                        components={{agDateInput: DTPicker, customNameCellEditor: CustomNameCellEditor}}
                        rowModelType={'serverSide'}
                        onGridReady={this.onGridReady}
                        onCellEditingStopped={this.cellEditingStoppedFunctionCaller}
                        rowSelection={this.props.rowSelection}
                        onSelectionChanged={() => {
                            const selectedRows = this.gridApi.getSelectedRows();
                            this.props.onClickRow && this.props.onClickRow(selectedRows);
                        }}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        maintainColumnOrder={true}
                        onFirstDataRendered={this.onFirstDataRendered.bind(this)}
                        onFilterChanged={(params)=> {
                            if(saveFilterChanges){
                                onFilterChangedHelper(params, gridFilterStateSessionVariableName, updateFortressAiReportGridFilterModelReactive);
                            }
                        }}
                        onSortChanged={this.onColumnStateChanged}
                        onColumnMoved={this.onColumnStateChanged}
                        onColumnVisible={this.onColumnStateChanged}
                        getContextMenuItems={this.getContextMenuItems}
                        sideBar={this.props.sideBar}
                    />
                </div>
            </div>
        );
    };
    cellEditingStoppedFunctionCaller = (event) => {
        let gridApi = event.api
        if(event.column.colId === "incidentDisplayName"){
            if(event.newValue && event.newValue.trim().length > 0){
                if(event.newValue === event.oldValue){
                    event.data.incidentDisplayName = event.oldValue
                    gridApi.refreshCells({suppressFlash: true})
                    return;
                }
                if(!event.data.fortressAiReportId || !event.data.zenGroupId){
                    NotificationManager.error("Error updating report name")
                    event.data.incidentDisplayName = event.oldValue
                    gridApi.refreshCells({suppressFlash: true})
                    return;
                }
                changeFortressAiReportNameReactive(event.newValue.trim(), event.data.zenGroupId, event.data.fortressAiReportId).then(function(response){
                    if(response){
                        NotificationManager.success("Successfully changed this report's name")
                        event.node.setDataValue("incidentDisplayName", event.newValue.trim());
                    }
                }).catch(function(error){
                    if(error.message){
                        NotificationManager.error(error.message)
                    }
                    else{
                        NotificationManager.error("Error changing report name")
                    }
                    event.data.incidentDisplayName = event.oldValue
                    gridApi.refreshCells({suppressFlash: true})
                })
            }
            else{
                event.data.incidentDisplayName = event.oldValue
                gridApi.refreshCells({suppressFlash: true})
            }
        } else if(event.column.colId === "zenGroupDisplayName"){
            handleGroupColumnChangeNameOnly(event)
        } else if(event.column.colId === "agentDisplayName"){
            if(event.newValue && event.newValue.trim().length > 0){
                if(event.newValue === event.oldValue){
                    event.data.agentDisplayName = event.oldValue
                    gridApi.refreshCells({suppressFlash: true})
                    return;
                }
                if(!event.data.agentId){
                    NotificationManager.error("There is no agent associated with this report.")
                    event.data.agentDisplayName = event.oldValue
                    gridApi.refreshCells({suppressFlash: true})
                    return;
                }
                if(!event.data.zenGroupId){
                    NotificationManager.error("There is no zenGroup associated with this agent.")
                    event.data.agentDisplayName = event.oldValue
                    gridApi.refreshCells({suppressFlash: true})
                    return;
                }
                changeAgentNameReactive(event.newValue.trim(), event.data.zenGroupId, event.data.agentId).then(function(response){
                    NotificationManager.success("Successfully changed this agent's name")
                    event.node.setDataValue("agentDisplayName", event.newValue.trim());
                }).catch(function(error){
                    if(error.message){
                        NotificationManager.error(error.message)
                    }
                    else{
                        NotificationManager.error("Error changing agent name")
                    }
                    event.data.agentDisplayName = event.oldValue
                    gridApi.refreshCells({suppressFlash: true})
                })
            }
            else{
                event.data.agentDisplayName = event.oldValue
                gridApi.refreshCells({suppressFlash: true})
            }
        }
    };
}

class DetailsGrid extends Component {
    constructor(props, onClickRow, filterVals, headers, data) {
        super(props);
    }
    onFirstDataRendered = (params) => {
        params.api.autoSizeAllColumns()
    };

    onGridReady = async (params) => {
        this.gridApi = params.api;
    }

    render() {
        return (
            <div className="mt-2">
                <div
                    id="detailsGrid"
                    className="ag-theme-alpine rounded-md shadow h-full w-full"
                >
                    <AgGridReact
                        columnMenu={"legacy"}
                        defaultColDef={{
                            resizable: true,
                            filterParams: null,
                            headerClass:"border-0 border-b-0",
                            cellClass:"outline:none",
                            enableCellChangeFlash: true,
                            autoHeight: false,
                            cellDataType: false
                        }}
                        columnDefs={this.props.headers}
                        suppressContextMenu={true}
                        suppressExcelExport={true}
                        suppressCsvExport={true}
                        modules={[
                            AllCommunityModule,
                            ClientSideRowModelModule,
                            ColumnMenuModule,
                            ContextMenuModule
                        ]}
                        suppressMultiSort={true}
                        domLayout="autoHeight"
                        headerHeight={50}
                        enableBrowserTooltips={true}
                        rowData={this.props.data}
                        rowSelection={this.props.rowSelection}
                        onGridReady={this.onGridReady}
                        onFirstDataRendered={this.onFirstDataRendered}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                    />
                </div>
            </div>
        );
    }
}